import { type Key } from "react";

export interface Product {
  // string | number: rows are keyed by their real catalog/product id (a UUID
  // string) whenever one exists, not just a sequential number, to avoid
  // colliding with catalog rows' own small-integer keys.
  key: Key;
  id: string;
  variety: string;
  manufacturer: string;
  measureUnit: string;
  density_range?: {
    max?: number;
    min?: number;
  };
  density_options?: number[];
}

export type DensityMode = "range" | "options";

export const DENSITY_MODES: Record<DensityMode, DensityMode> = {
  range: "range",
  options: "options",
};

export interface DensityRangeValues {
  min?: number | null;
  max?: number | null;
}

export interface ProductCardProps extends Product {
  globalDensityRange: boolean;
  globalDensityValues?: DensityRangeValues | null;
  globalDensityMode?: DensityMode;
  onDeleteProduct: (key: Key) => void;
  productKey: Key;
  updateProductDensityRange: (
    productId: string,
    densityType: "max" | "min",
    value: number
  ) => void;
  productDensityRangeMin?: number;
  productDensityRangeMax?: number;
  updateProductDensityOptions: (productId: string, values: number[]) => void;
  productDensityOptions?: number[];
}

export interface createProtocolFormType {
  // The Trial types step is multi-select, so the form always holds an array of
  // types (matching `createProtocolValidationDataType.protocolType` below).
  protocolType: string[] | null;
  protocolName: string;
  protocolDescription: string;
  crop: string;
  plotMinWidth: number;
  plotMinLength: number;
  plotRecommendedWidth: number;
  plotRecommendedHeight: number;
  crop_details: {
    country: string;
    countries?: string[];
    crop_id: string;
  };
  objectives: Array<{
    name: string;
    description: string;
  }>;
  product_details: {
    seed: {
      density_range: {
        max: number;
        min: number;
      };
      global_density_range: boolean;
      measure_unit: string;
      density_options?: number[];
      products: Array<{
        density_range?: {
          max: number;
          min: number;
        };
        density_options?: number[];
        manufacturer: string;
        measureUnit: string;
        variety: string;
      }>;
    };
    nutrient: {
      application_range: {
        max: number;
        min: number;
      };
      interval_distance: number;
      number_of_intervals: number;
      products: Array<{
        nutrient_code: string;
      }>;
    };
    biologicals: {
      treatments: Array<{
        tank_mix_rate: {
          max: number;
          min: number;
        };
        products: Array<{
          id: string;
          rate: number;
          variety: string;
          measure_unit: string;
        }>;
      }>;
      applications: Array<{
        description: string;
      }>;
      seed_details?: {
        products?: Product[];
        measure_unit?: string;
      };
      nutrient_details?: {
        products?: any[];
        measure_unit?: string;
      };
      biologicals?: {
        products?: any[];
        measure_unit?: string;
        application_methods?: string[];
      };
    };
  };
}

export interface protocolCountries {
  country_name: string;
  id: string;
  iso3166_alpha2_code: string;
}

// Immutable snapshot of the ORIGINAL template selections, captured once during
// the template fetch in the CreateProtocol provider. Used to fully restore the
// crop + seed + biological products when the user navigates the country back to
// the one the template was originally created with.
export interface OriginalTemplateSnapshot {
  country?: string | null;
  cropName?: string | null;
  cropId?: string | null;
  localizedCropIds?: string[];
  seedDetails?: any | null;
  biologicals?: any | null;
}

export interface createProtocolValidationDataType {
  isEditMode?: boolean;
  productsValid?: boolean;
  protocolType?: string[];
  objectives?: Array<{
    name: string;
    description: string;
  }>;
  density_range?: { min: number | null; max: number | null };
  density_options?: number[];
  globalDensityRange?: boolean;
  globalDensityMode?: DensityMode;
  globalDensityOptions?: number[];
  products?: Product[];
  measure_unit?: string;
  nutrients?: any;
  isNutrientDataValid?: boolean;
  isNewProtocol?: boolean;
  biologicalProducts?: Array<{
    id: string;
    rate: number;
    variety: string;
    measure_unit: string;
  }>;
  biologicalApplications?: Array<{ description: string }>;
  biologicalTankMixRate?: { min: number; max: number };
  status?: string;
  complete?: boolean;
  // Set true when the user changes Country on a template-loaded protocol.
  // Drives the "Change Country Alert" on the Rules & Products screens.
  showCountryChangeAlert?: boolean;
  // Set when a template country change clears the crop (Scenario B):
  // all seed + biological products and their calculations must be wiped.
  clearProductsAndCalculations?: boolean;
  // Set when the crop name still exists in the new country (Scenario A):
  // each product screen must re-validate its selections against the new
  // country's catalog and clear them on any mismatch (Scenario A.2).
  seedRevalidationRequired?: boolean;
  bioRevalidationRequired?: boolean;
  // Seeds: true while the revalidation catalog fetch is in flight / being
  // processed. Ensures the seed revalidation-result effect only acts once the
  // new country's catalog has resolved.
  seedRevalidationProcessing?: boolean;
  // Carries the matched crop id for the new country so the Products screen can
  // re-fetch the catalog for revalidation.
  revalidationCropId?: string;
  revalidationLocalizedCropIds?: string[];
  // New ISO country code (Scenario A) used by the biological/CP product screen
  // to fetch the correct catalog when revalidating. Null clears any stale value.
  revalidationCountry?: string | null;
  // Set true when the user returns to the ORIGINAL template country: the seed
  // and biological product screens must restore their original selections from
  // `originalTemplateSnapshot` rather than revalidate or clear.
  restoreOriginalSeeds?: boolean;
  restoreOriginalBio?: boolean;
  // Authoritative, immutable snapshot of the template's original crop/products.
  // Captured once during the template fetch; never mutated afterwards.
  originalTemplateSnapshot?: OriginalTemplateSnapshot;
  // True from the moment the user changes Country until BasicInfoComponent's
  // crop list + seed-variety catalog calls resolve and the keep-vs-clear
  // decision is applied. Blocks the wizard's next-step button so the user
  // can't advance while it's still unknown whether the selected crop is
  // valid for the new country.
  cropRevalidationInProgress?: boolean;
}

export const defaultCreateProtocolValidationData: createProtocolValidationDataType =
  {
    isNewProtocol: true,
  };

export interface ProtocolResponse {
  data: {
    name: string;
    id: string;
    crop: string;
    objectives: Array<{
      description: string;
      name: string;
      notes: string;
      target: string[];
    }>;
    org_id: string;
    plot_minimum_size: {
      length: number;
      width: number;
    };
    plot_recommended_size: {
      length: number;
      width: number;
    };
    strip_size: {
      min_width: number;
      max_width: number;
    };
    plot_type: string;
    trial_application_method: string[];
    product_details: {
      seeds: {
        density_range: {
          max: number;
          min: number;
        };
        global_density_range: boolean;
        measure_unit: string;
        density_options?: number[];
        products: Array<{
          density_range?: {
            max: number;
            min: number;
          };
          density_options?: number[];
          manufcaturer: string;
          measure_unit: string;
          variety: string;
        }>;
      };
    };
    protocol_type: string[];
    status: "published" | "draft";
  };
}

export interface biologicalsInterface {
  treatments: Array<{
    tank_mix_rate: {
      max: number;
      min: number;
    };
    products: Array<{
      id: string;
      rate: number;
      variety: string;
      measure_unit: string;
    }>;
  }>;
  products: Array<{
    id: string;
    rate: number;
    variety: string;
    measure_unit: string;
  }>;
  applications: Array<{ description: string }>;
  tank_mix_rate?: { min: number; max: number };
}
